import { useMutation, useQueryClient } from '@tanstack/react-query';

import { APIClientBase } from ':cloud/state/Api';

import { QueryKey } from '../types';

type GetPaymasterInfoResponse = {
  chainId: number;
  ethSponsored: number;
  organizationId: string;
  projectId: string;
  sponsoredTransactionCount: number;
  usdSponsored: number;
};

type GetPaymasterInfoProps = {
  organizationId: string;
  projectId: string;
  isTestnet?: boolean;
};

async function getPaymasterInfo({
  organizationId,
  projectId,
  isTestnet,
}: GetPaymasterInfoProps): Promise<GetPaymasterInfoResponse> {
  const { data } = await APIClientBase.post<GetPaymasterInfoResponse>(
    '/rpc/api/paymaster/projectInfo',
    {
      organizationId,
      projectId,
      chainId: isTestnet ? 84532 : 8453,
    },
  );
  return data;
}

type UseGetPaymasterInfoProps = {
  onSuccess?: (sponsoredGas: number) => void;
  organizationId?: string;
  projectId?: string;
};

export function useGetPaymasterInfo({
  onSuccess,
  organizationId = '',
  projectId = '',
}: UseGetPaymasterInfoProps) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (isTestnet: boolean) =>
      getPaymasterInfo({ organizationId, projectId, isTestnet }),
    onSuccess: async (data) => {
      onSuccess?.(data?.usdSponsored);
      queryClient.setQueryData([QueryKey.paymasterInfo, organizationId, projectId], data);
    },
    meta: {
      errorMsg: `There was an error fetching paymaster info`,
    },
  });
}
